// modules/freshcards/src/core/overlays/lifecycle.ts
//
// 2026-09-18 (PROMPT F 19 Phase 3b) — OverlayLifecycle = state machine + hooks
// for overlay registration, activation, and error handling.
//
// 3 states (registered | active | suspended) +
// 6 events (registered | unregistered | activated | deactivated | suspended | error) +
// 6 hooks (onRegister | onUnregister | onActivate | onDeactivate | onSuspend | onError).

import type { AppOverlay } from '../../types/models/AppOverlay';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** Lifecycle state of an overlay in the registry. */
export type OverlayState = 'registered' | 'active' | 'suspended';

/** Reason an overlay is in 'suspended' state. */
export type SuspensionReason =
  | 'incompatible_version' // FreshCards version not in compatibility range
  | 'load_error'           // failed to load theme/layout bundle
  | 'validation_failed'    // manifest validation error
  | 'dependency_missing';  // requires another overlay not registered

/** Scope of an activation (which cascade level). */
export interface OverlayScope {
  userId?: string;
  projectId?: string;
  surfaceId?: string;
}

/** Filter shape for list(). */
export interface OverlayFilter {
  flavor?: 'theme' | 'layout' | 'bundle';
  tags?: string[];
  author?: string;
  distribution?: 'proprietary' | 'off-the-shelf' | 'generic';
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** Lifecycle event payload. */
export type OverlayLifecycleEvent =
  | { type: 'overlay:registered'; overlayId: string; ts: number }
  | { type: 'overlay:unregistered'; overlayId: string; ts: number }
  | { type: 'overlay:activated'; overlayId: string; scope: OverlayScope; ts: number }
  | { type: 'overlay:deactivated'; overlayId: string; scope: OverlayScope; ts: number }
  | { type: 'overlay:suspended'; overlayId: string; reason: SuspensionReason; ts: number }
  | { type: 'overlay:error'; overlayId: string; error: string; ts: number };

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Lifecycle hooks — caller can register one or all. */
export interface OverlayLifecycleHooks {
  /** Fired when an overlay is registered. */
  onRegister?: (overlay: AppOverlay) => void | Promise<void>;
  /** Fired when an overlay is unregistered. */
  onUnregister?: (overlayId: string) => void | Promise<void>;
  /** Fired when an overlay is activated at a scope. */
  onActivate?: (overlayId: string, scope: OverlayScope) => void | Promise<void>;
  /** Fired when an overlay is deactivated. */
  onDeactivate?: (overlayId: string, scope: OverlayScope) => void | Promise<void>;
  /** Fired when an overlay is suspended (compatibility/load/validation). */
  onSuspend?: (overlayId: string, reason: SuspensionReason) => void | Promise<void>;
  /** Fired when an overlay errors during use. */
  onError?: (overlayId: string, error: string) => void | Promise<void>;
}

// ---------------------------------------------------------------------------
// State accessor helpers (used by registry)
// ---------------------------------------------------------------------------

/** Tracks state for each registered overlay id. */
export type OverlayStateMap = Map<string, OverlayState>;

export function newStateMap(): OverlayStateMap {
  return new Map();
}

export function setState(map: OverlayStateMap, id: string, state: OverlayState): void {
  map.set(id, state);
}

export function getState(map: OverlayStateMap, id: string): OverlayState {
  return map.get(id) ?? 'registered';
}
